import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CheckCircle2, XCircle, ArrowRight, Layers } from 'lucide-react';
import type { QuizData, MultipleChoiceQuiz, FillInBlankQuiz } from './parseQuizBlocks';

// ---- Fuzzy matching helpers for fill-in-the-blank answers ----

const ARTICLES = new Set(['the', 'a', 'an']);

/** Normalise an answer string: trim, lowercase, strip leading articles. */
function normalizeAnswer(s: string): string {
  const words = s.trim().toLowerCase().split(/\s+/);
  const stripped = words.length > 1 && ARTICLES.has(words[0]) ? words.slice(1) : words;
  return stripped.join(' ');
}

/** Levenshtein distance between two strings. */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0) as number[]);
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

/** Returns true if `input` matches `answer` exactly or within Levenshtein distance 1. */
function fuzzyMatch(input: string, answer: string): boolean {
  if (input === answer) return true;
  // Only allow typo tolerance for answers longer than 3 characters
  if (answer.length > 3 && levenshtein(input, answer) <= 1) return true;
  return false;
}

interface QuizBlockProps {
  data: QuizData;
  onCorrect: () => void;
  onContinue?: () => void;
  onGoDeeper?: () => void;
}

export function QuizBlock({ data, onCorrect, onContinue, onGoDeeper }: QuizBlockProps) {
  if (data.type === 'multiple_choice') {
    return (
      <MultipleChoiceQuizBlock
        data={data}
        onCorrect={onCorrect}
        onContinue={onContinue}
        onGoDeeper={onGoDeeper}
      />
    );
  }
  return (
    <FillInBlankQuizBlock
      data={data}
      onCorrect={onCorrect}
      onContinue={onContinue}
      onGoDeeper={onGoDeeper}
    />
  );
}

// ---------------------------------------------------------------------------
// Multiple Choice
// ---------------------------------------------------------------------------

type MCState = 'unanswered' | 'incorrect' | 'correct';

function MultipleChoiceQuizBlock({
  data,
  onCorrect,
  onContinue,
  onGoDeeper,
}: {
  data: MultipleChoiceQuiz;
  onCorrect: () => void;
  onContinue?: () => void;
  onGoDeeper?: () => void;
}) {
  const [state, setState] = useState<MCState>('unanswered');
  const [disabledOptions, setDisabledOptions] = useState<Set<number>>(new Set());
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleSelect = (index: number) => {
    if (state === 'correct') return;

    setSelectedIndex(index);

    if (index === data.correctIndex) {
      setState('correct');
      onCorrect();
    } else {
      setState('incorrect');
      setDisabledOptions((prev) => new Set(prev).add(index));
    }
  };

  return (
    <div className="my-4 rounded-xl border border-border bg-card p-4">
      <p className="font-medium mb-3">{data.question}</p>
      <div className="space-y-2">
        {data.options.map((option, idx) => {
          const isDisabled = disabledOptions.has(idx);
          const isCorrectAnswer = state === 'correct' && idx === data.correctIndex;
          const isWrongSelection = state === 'incorrect' && idx === selectedIndex;

          return (
            <Button
              key={idx}
              variant="outline"
              className={cn(
                'w-full justify-start text-left h-auto py-2 px-3 whitespace-normal',
                isCorrectAnswer &&
                  'border-green-500 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100',
                isWrongSelection &&
                  !isDisabled &&
                  'border-red-500 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100',
                isDisabled && 'opacity-50 cursor-not-allowed',
              )}
              disabled={isDisabled || state === 'correct'}
              onClick={() => handleSelect(idx)}
            >
              <span className="mr-2 font-mono text-xs text-muted-foreground">
                {String.fromCharCode(65 + idx)}.
              </span>
              {option}
              {isCorrectAnswer && (
                <CheckCircle2 className="ml-auto h-4 w-4 text-green-600 flex-shrink-0" />
              )}
              {isWrongSelection && !isDisabled && (
                <XCircle className="ml-auto h-4 w-4 text-red-600 flex-shrink-0" />
              )}
            </Button>
          );
        })}
      </div>
      {state !== 'unanswered' && (
        <div
          className={cn(
            'mt-3 rounded-lg p-3 text-sm',
            state === 'correct'
              ? 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'
              : 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
          )}
        >
          {state === 'correct' ? (
            <span className="font-medium">Correct! </span>
          ) : (
            <span className="font-medium">Not quite. </span>
          )}
          {data.explanation}
        </div>
      )}
      {state === 'correct' && (onContinue || onGoDeeper) && (
        <div className="mt-3 flex justify-end gap-2">
          {onGoDeeper && (
            <Button size="sm" variant="outline" onClick={onGoDeeper} className="gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              Go Deeper
            </Button>
          )}
          {onContinue && (
            <Button size="sm" onClick={onContinue} className="gap-1.5">
              Continue
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fill in the Blank
// ---------------------------------------------------------------------------

type FITBState = 'unanswered' | 'incorrect' | 'correct';

function FillInBlankQuizBlock({
  data,
  onCorrect,
  onContinue,
  onGoDeeper,
}: {
  data: FillInBlankQuiz;
  onCorrect: () => void;
  onContinue?: () => void;
  onGoDeeper?: () => void;
}) {
  const [state, setState] = useState<FITBState>('unanswered');
  const [inputValue, setInputValue] = useState('');

  const allAcceptable = [data.answer, ...(data.acceptableAnswers || [])];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'correct' || !inputValue.trim()) return;

    const normalised = normalizeAnswer(inputValue);
    const isCorrect = allAcceptable.some((a) => fuzzyMatch(normalised, normalizeAnswer(a)));

    if (isCorrect) {
      setState('correct');
      onCorrect();
    } else {
      setState('incorrect');
    }
  };

  return (
    <div className="my-4 rounded-xl border border-border bg-card p-4">
      <p className="font-medium mb-3">{data.question}</p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={state === 'correct'}
          placeholder="Type your answer..."
          className={cn(
            'flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring',
            state === 'correct' && 'border-green-500',
            state === 'incorrect' && 'border-red-500',
          )}
        />
        <Button type="submit" size="sm" disabled={state === 'correct' || !inputValue.trim()}>
          {state === 'correct' ? 'Correct' : 'Check'}
        </Button>
      </form>
      {state !== 'unanswered' && (
        <div
          className={cn(
            'mt-3 rounded-lg p-3 text-sm',
            state === 'correct'
              ? 'bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200'
              : 'bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200',
          )}
        >
          {state === 'correct' ? (
            <span className="font-medium">Correct! </span>
          ) : (
            <span className="font-medium">Try again. </span>
          )}
          {data.explanation}
        </div>
      )}
      {state === 'correct' && (onContinue || onGoDeeper) && (
        <div className="mt-3 flex justify-end gap-2">
          {onGoDeeper && (
            <Button size="sm" variant="outline" onClick={onGoDeeper} className="gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              Go Deeper
            </Button>
          )}
          {onContinue && (
            <Button size="sm" onClick={onContinue} className="gap-1.5">
              Continue
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
